import type { VehicleCreatePayload } from "@/customTypings/vehicle/Vehicle";
import { authHeader, handleResponse } from "..";

export const VEHICLE_CREATE_CONFLICT = {
    duplicateActive: "vehicle.duplicateActive",
    deletedRestoreAvailable: "vehicle.deletedRestoreAvailable",
} as const;

export type VehicleDeletedRestoreAvailableConflict = {
    code: string;
    message: string;
    existingId: string;
    existingRegistration: string;
};

export function isVehicleDeletedRestoreAvailableConflict(
    value: unknown,
    expectedCode: string
): value is VehicleDeletedRestoreAvailableConflict {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as VehicleDeletedRestoreAvailableConflict).code === expectedCode &&
        typeof (value as VehicleDeletedRestoreAvailableConflict).existingId === "string"
    );
}

export class VehicleCreateConflictError extends Error {
    readonly conflict: VehicleDeletedRestoreAvailableConflict;

    constructor(conflict: VehicleDeletedRestoreAvailableConflict) {
        super(conflict.message);
        this.name = "VehicleCreateConflictError";
        this.conflict = conflict;
    }
}

export type VehicleRestoreDeletedPayload = VehicleCreatePayload & { id: string };

export async function postVehicleCreate<TDetail>(
    createUrl: string,
    payload: VehicleCreatePayload
): Promise<TDetail> {
    const response = await fetch(createUrl, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        /* non-JSON */
    }

    if (
        response.status === 409 &&
        isVehicleDeletedRestoreAvailableConflict(data, VEHICLE_CREATE_CONFLICT.deletedRestoreAvailable)
    ) {
        throw new VehicleCreateConflictError(data);
    }

    return handleResponse({
        text: async () => text,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
    }) as Promise<TDetail>;
}
